import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { openRazorpay } from '@/lib/razorpay';
import { toast } from 'sonner';
import { CONSULT } from '@/constants/testIds';

const Consultation = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [doctor, setDoctor] = useState(null);
  const [fee, setFee] = useState({ amount: 1000, currency: 'INR' });
  const [days, setDays] = useState([]);
  const [activeDay, setActiveDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [reason, setReason] = useState('');
  const [step, setStep] = useState('select'); // select | payment | confirmed
  const [processing, setProcessing] = useState(false);
  const [confirmedAppt, setConfirmedAppt] = useState(null);

  useEffect(() => {
    api.get('/consultations/doctor').then(({ data }) => setDoctor(data));
    api.get('/consultations/fee').then(({ data }) => setFee(data));
    api.get('/consultations/available-slots').then(({ data }) => {
      setDays(data.days);
      if (data.days.length) setActiveDay(data.days[0].date);
    });
  }, []);

  const proceedToPayment = async () => {
    if (!selectedSlot) { toast.error('Choose a time slot'); return; }
    if (!user) {
      nav(`/login?next=${encodeURIComponent('/consultation')}`);
      return;
    }
    // Free consultation → skip Razorpay entirely and book immediately.
    if (!fee.amount || fee.amount <= 0) {
      setProcessing(true);
      try {
        const { data: booking } = await api.post('/consultations/book', {
          slot_datetime: selectedSlot,
          reason: reason || null,
        });
        setConfirmedAppt(booking.appointment);
        setStep('confirmed');
      } catch (e) {
        toast.error(e?.response?.data?.detail || e?.message || 'Booking failed');
      } finally { setProcessing(false); }
      return;
    }
    setStep('payment');
  };

  const payAndBook = async () => {
    setProcessing(true);
    try {
      const { payment_id } = await openRazorpay({
        amount: fee.amount,
        purpose: 'consultation',
        description: `Consultation with ${doctor?.name || 'Ayurvedic doctor'}`,
        name: user?.name,
        email: user?.email,
      });
      // Book after verified payment
      const { data: booking } = await api.post('/consultations/book', {
        slot_datetime: selectedSlot,
        reason: reason || null,
        payment_id,
      });
      setConfirmedAppt(booking.appointment);
      setStep('confirmed');
    } catch (e) {
      if (e?.message === 'Payment cancelled') {
        toast.message('Payment cancelled');
      } else {
        toast.error(e?.response?.data?.detail || e?.message || 'Booking failed');
      }
    } finally {
      setProcessing(false);
    }
  };

  const activeDayObj = days.find((d) => d.date === activeDay);

  return (
    <div className="bg-sans_bone">
      <section className="sans-section">
        <div className="sans-container max-w-6xl">
          <div className="sans-label mb-6">Online Consultation</div>
          <h1 className="sans-h1">Speak with an Ayurvedic doctor.</h1>
          <p className="sans-body mt-6 max-w-xl">
            A private 30–40 minute video consultation with a BAMS-qualified physician.
            Personal, considered, rooted in classical Ayurveda.
          </p>

          {step === 'select' && (
            <div className="mt-16 grid md:grid-cols-12 gap-12">
              <div className="md:col-span-8">
                {doctor && (
                  <div className="bg-white border border-sans_line p-8 mb-10">
                    <div className="flex items-start justify-between gap-6">
                      <div>
                        <div className="sans-label mb-3">Your doctor</div>
                        <div className="font-serif text-2xl md:text-3xl text-sans_ink">{doctor.name}</div>
                        <div className="text-sm text-sans_mute mt-2">{doctor.credentials}</div>
                        <div className="text-sm text-sans_mute mt-1">{doctor.specialization}</div>
                        <div className="text-xs uppercase tracking-[0.2em] text-sans_mute mt-4">{doctor.experience_years}+ years experience</div>
                      </div>
                      <div className="text-right">
                        <div className="sans-label mb-1">Fee</div>
                        <div className="font-serif text-3xl text-sans_ink">{fee.amount > 0 ? `₹${fee.amount}` : 'Free'}</div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="sans-label mb-4">Choose a date</div>
                <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
                  {days.map((d) => (
                    <button
                      key={d.date}
                      data-testid={CONSULT.dayTab(d.date)}
                      onClick={() => { setActiveDay(d.date); setSelectedSlot(null); }}
                      className={`flex-shrink-0 px-5 py-4 border transition-colors ${
                        activeDay === d.date ? 'bg-sans_ink text-sans_bone border-sans_ink' : 'bg-white text-sans_ink border-sans_line hover:border-sans_ink'
                      }`}
                    >
                      <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{d.day_label}</div>
                      <div className="font-serif text-lg mt-1">{d.date_label}</div>
                    </button>
                  ))}
                </div>

                <div className="mt-10 sans-label mb-4">Available times</div>
                {activeDayObj && (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                    {activeDayObj.slots.map((s) => (
                      <button
                        key={s.datetime}
                        disabled={!s.available}
                        data-testid={CONSULT.slotBtn(s.datetime)}
                        onClick={() => setSelectedSlot(s.datetime)}
                        className={`py-3.5 text-sm tracking-wide border transition-colors ${
                          !s.available
                            ? 'bg-sans_line/40 text-sans_mute line-through border-transparent cursor-not-allowed'
                            : selectedSlot === s.datetime
                              ? 'bg-sans_ink text-sans_bone border-sans_ink'
                              : 'bg-white text-sans_ink border-sans_line hover:border-sans_ink'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}

                <div className="mt-10">
                  <label className="sans-label block mb-3">Anything you'd like the doctor to know? (optional)</label>
                  <textarea
                    data-testid={CONSULT.reasonInput}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    placeholder="e.g. persistent digestive discomfort, sleep concerns…"
                    className="w-full bg-white border border-sans_line p-4 text-sm text-sans_ink placeholder:text-sans_mute focus:outline-none focus:border-sans_ink transition-colors"
                  />
                </div>
              </div>

              <div className="md:col-span-4">
                <div className="border border-sans_line bg-white p-8 sticky top-24">
                  <div className="sans-label mb-6">Booking summary</div>
                  <div className="space-y-4 text-sm">
                    <div className="flex justify-between"><span className="text-sans_mute">When</span><span className="text-sans_ink text-right">{selectedSlot ? new Date(selectedSlot).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-sans_mute">Doctor</span><span className="text-sans_ink text-right">{doctor?.name || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-sans_mute">Fee</span><span className="text-sans_ink">{fee.amount > 0 ? `₹${fee.amount}` : 'Free'}</span></div>
                  </div>
                  <button
                    onClick={proceedToPayment}
                    data-testid={CONSULT.confirmBtn}
                    disabled={!selectedSlot || processing}
                    className="sans-btn-primary w-full mt-8 disabled:opacity-40"
                  >
                    {processing ? 'Booking…' : (fee.amount > 0 ? 'Continue to payment' : 'Confirm booking')}
                  </button>
                  <p className="mt-4 text-xs text-sans_mute leading-relaxed">Free cancellation up to 12 hours before your slot.</p>
                </div>
              </div>
            </div>
          )}

          {step === 'payment' && (
            <div className="mt-16 max-w-xl mx-auto bg-white border border-sans_line p-10">
              <div className="sans-label mb-4">Payment</div>
              <h2 className="font-serif text-3xl text-sans_ink">Confirm your consultation</h2>
              <div className="mt-8 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-sans_mute">When</span><span className="text-sans_ink">{new Date(selectedSlot).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span></div>
                <div className="flex justify-between"><span className="text-sans_mute">Doctor</span><span className="text-sans_ink">{doctor?.name}</span></div>
                <div className="border-t border-sans_line pt-3 flex justify-between font-serif text-lg"><span className="text-sans_ink">Total</span><span className="text-sans_ink">₹{fee.amount}</span></div>
              </div>
              <div className="mt-6 p-4 bg-sans_bone border border-sans_line text-xs text-sans_mute leading-relaxed">
                <strong className="text-sans_ink">Secure payment via Razorpay</strong> — UPI, cards, netbanking & wallets. Test card <code className="text-sans_ink">4111 1111 1111 1111</code>, any future date & CVV.
              </div>
              <button onClick={payAndBook} disabled={processing} data-testid={CONSULT.payBtn} className="sans-btn-primary w-full mt-8">
                {processing ? 'Processing…' : `Pay ₹${fee.amount} & confirm`}
              </button>
              <button onClick={() => setStep('select')} className="w-full mt-4 text-sm text-sans_mute hover:text-sans_ink">← Back</button>
            </div>
          )}

          {step === 'confirmed' && confirmedAppt && (
            <div className="mt-16 max-w-2xl mx-auto text-center">
              <div className="sans-label mb-6">Confirmed</div>
              <h2 className="sans-h1">Your consultation is booked.</h2>
              <div className="sans-rule mx-auto mt-8" />
              <div className="mt-12 bg-white border border-sans_line p-10 text-left">
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between"><span className="text-sans_mute">Doctor</span><span className="text-sans_ink">{confirmedAppt.doctor_name}</span></div>
                  <div className="flex justify-between"><span className="text-sans_mute">When</span><span className="text-sans_ink">{new Date(confirmedAppt.slot_datetime).toLocaleString('en-IN', { dateStyle: 'full', timeStyle: 'short' })}</span></div>
                  <div className="flex justify-between items-center"><span className="text-sans_mute">Meeting link</span><a href={confirmedAppt.meet_link} className="sans-link text-sans_ink text-right break-all">{confirmedAppt.meet_link}</a></div>
                </div>
                <p className="mt-8 text-xs text-sans_mute leading-relaxed">A confirmation with the meeting link has been sent to your email. You can view or reschedule from your dashboard.</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <button onClick={() => nav('/dashboard')} className="sans-btn-primary">View my dashboard</button>
                <button onClick={() => nav('/')} className="sans-btn-secondary">Return home</button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Consultation;
